import { useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import moment from 'moment';
import DocumentViewerModal from '../components/DocumentViewerModal';
import BulkUploadModal from '../components/BulkUploadModal';
import { UploadIcon } from '@heroicons/react/outline';

export default function Policies() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isFormatModalOpen, setIsFormatModalOpen] = useState(false);
  const [selectedPolicyForGeneration, setSelectedPolicyForGeneration] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('a4');
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPolicyForPayment, setSelectedPolicyForPayment] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedPolicyForStatus, setSelectedPolicyForStatus] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPolicyForDelete, setSelectedPolicyForDelete] = useState(null);
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/api/policies', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPolicies(response.data);
    } catch (error) {
      console.error('Error fetching policies:', error);
      toast.error(t('policies.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LYD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'bg-gray-100 text-gray-800',
      yet_to_start: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      void: 'bg-red-100 text-red-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${statusClasses[status]}`}>
        {t(`policies.status.${status}`)}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    return (
      <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
        paymentStatus === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {t(`policies.paymentStatus.${paymentStatus}`)}
      </span>
    );
  };

  const handlePreview = (policy) => {
    setSelectedPolicy(policy);
    setIsPreviewOpen(true);
  };

  const handleGenerateClick = (policy) => {
    if (policy.paymentStatus !== 'paid') {
      toast.error(t('policies.mustBePaid'));
      return;
    }
    // Open format selection modal
    setSelectedPolicyForGeneration(policy);
    setIsFormatModalOpen(true);
  };

  const handleGenerate = async (format) => {
    if (!selectedPolicyForGeneration) return;

    setIsGenerating(true);
    setIsFormatModalOpen(false);
    
    try {
      const response = await api.post(
        `/api/documents/generate-from-policy/${selectedPolicyForGeneration.id}`,
        { format },
        {
          responseType: 'blob'
        }
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Policy_${selectedPolicyForGeneration.policyNumber}_${format.toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(t('policies.documentGenerated'));
      
      // Refresh policies to update hasGeneratedDocument flag
      fetchPolicies();
    } catch (error) {
      console.error('Document generation failed:', error);
      toast.error(error.message || t('policies.generateError'));
    } finally {
      setIsGenerating(false);
      setSelectedPolicyForGeneration(null);
    }
  };

  const handleView = (policy) => {
    setSelectedPolicy(policy);
    setIsViewModalOpen(true);
    setOpenMenuId(null);
  };

  const handleEdit = (policy) => {
    setOpenMenuId(null);
    // TODO: Implement edit functionality
    // The edit should open a modal to create a NEW policy with pre-filled data
    // and mark the original as void by passing originalPolicyId
    toast.error('Edit feature not yet implemented. Please create a new policy and mark this one as void manually.', {
      duration: 5000
    });
    console.log('Edit policy:', policy);
    // When implementing: Pass policy data and originalPolicyId to the policy creation modal
  };

  const handleMarkAsPaid = (policy) => {
    setOpenMenuId(null);
    // Open payment method modal
    setSelectedPolicyForPayment(policy);
    setIsPaymentModalOpen(true);
  };

  const handleChangeStatus = (policy) => {
    setOpenMenuId(null);
    setSelectedPolicyForStatus(policy);
    setSelectedStatus(policy.status);
    setIsStatusModalOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedPolicyForStatus) return;

    try {
      const token = localStorage.getItem('token');
      await api.patch(`/api/policies/${selectedPolicyForStatus.id}/status`, 
        { status: selectedStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Policy status updated successfully');
      setIsStatusModalOpen(false);
      setSelectedPolicyForStatus(null);
      fetchPolicies();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Failed to update policy status');
    }
  };

  const handleDelete = (policy) => {
    setOpenMenuId(null);
    setSelectedPolicyForDelete(policy);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPolicyForDelete) return;

    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/policies/${selectedPolicyForDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Policy deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedPolicyForDelete(null);
      fetchPolicies();
    } catch (error) {
      console.error('Error deleting policy:', error);
      toast.error(error.response?.data?.message || 'Failed to delete policy');
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedPolicyForPayment) return;

    try {
      const token = localStorage.getItem('token');
      await api.put(`/api/policies/${selectedPolicyForPayment.id}/payment`, 
        { 
          paymentStatus: 'paid',
          paymentMethod: selectedPaymentMethod
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Policy marked as paid (${selectedPaymentMethod})`);
      setIsPaymentModalOpen(false);
      setSelectedPolicyForPayment(null);
      fetchPolicies();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error(error.response?.data?.message || 'Failed to update payment status');
    }
  };

  const toggleMenu = (policyId, event) => {
    setOpenMenuId(openMenuId === policyId ? null : policyId);
  };

  const filteredPolicies = policies.filter(policy => {
    const matchesSearch = 
      policy.policyNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${policy.firstName} ${policy.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      policy.vehiclePlate?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || policy.status === statusFilter;
    const matchesPayment = paymentStatusFilter === 'all' || policy.paymentStatus === paymentStatusFilter;
    
    return matchesSearch && matchesStatus && matchesPayment;
  });

  if (loading) {
    return <div className="flex justify-center items-center h-full">{t('common.loading')}</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">{t('policies.title')}</h1>
          <p className="mt-2 text-sm text-gray-700">
            {t('policies.description')}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => setIsBulkUploadModalOpen(true)}
            className="btn-primary inline-flex items-center"
          >
            <UploadIcon className="h-5 w-5 mr-2" />
            {t('bulkUpload.bulkUpload')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <input
            type="text"
            placeholder={t('policies.searchPlaceholder')}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div>
          <select
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t('policies.allStatuses')}</option>
            <option value="draft">{t('policies.status.draft')}</option>
            <option value="yet_to_start">{t('policies.status.yet_to_start')}</option>
            <option value="active">{t('policies.status.active')}</option>
            <option value="expired">{t('policies.status.expired')}</option>
            <option value="void">{t('policies.status.void')}</option>
            <option value="cancelled">{t('policies.status.cancelled')}</option>
          </select>
        </div>
        <div>
          <select
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
          >
            <option value="all">{t('policies.allPaymentStatuses')}</option>
            <option value="paid">{t('policies.paymentStatus.paid')}</option>
            <option value="unpaid">{t('policies.paymentStatus.unpaid')}</option>
          </select>
        </div>
      </div>

      {/* Policies Table */}
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('policies.policyNumber')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('policies.clientName')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('policies.policyType')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('policies.vehiclePlate')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('policies.startDate')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('policies.endDate')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('policies.premium')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('policies.status.title')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('policies.paymentStatus.title')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('policies.document')}
                    </th>
                    <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                      {t('policies.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredPolicies.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="px-3 py-10 text-center text-sm text-gray-500">
                        {t('policies.noPolicies')}
                      </td>
                    </tr>
                  ) : (
                    filteredPolicies.map((policy) => (
                      <tr key={policy.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-primary-600">
                          {policy.policyNumber}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                          {`${policy.firstName || ''} ${policy.fatherName || ''} ${policy.grandfatherName || ''} ${policy.lastName || ''}`.trim() || 'N/A'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {policy.policyType}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {policy.vehiclePlate || 'N/A'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {policy.startDate ? moment(policy.startDate).format('DD/MM/YYYY HH:mm') : 'N/A'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {policy.endDate ? moment(policy.endDate).format('DD/MM/YYYY HH:mm') : 'N/A'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {formatCurrency(policy.totalPremium)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {getStatusBadge(policy.status)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {getPaymentStatusBadge(policy.paymentStatus)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          {policy.hasGeneratedDocument ? (
                            <span className="text-green-600">✓ {t('policies.generated')}</span>
                          ) : (
                            <span className="text-gray-400">{t('policies.notGenerated')}</span>
                          )}
                        </td>
                        <td className="relative whitespace-nowrap px-3 py-4 text-sm text-right">
                          <div className="flex items-center justify-end space-x-2">
                            {/* Quick Actions */}
                            <button
                              onClick={() => handlePreview(policy)}
                              disabled={policy.paymentStatus !== 'paid'}
                              className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Preview"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>

                            {!policy.hasGeneratedDocument && (
                              <button
                                onClick={() => handleGenerateClick(policy)}
                                disabled={policy.paymentStatus !== 'paid' || isGenerating}
                                className="inline-flex items-center px-2 py-1 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Generate"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </button>
                            )}

                            {policy.hasGeneratedDocument && (
                              <button
                                onClick={() => handleGenerateClick(policy)}
                                disabled={policy.paymentStatus !== 'paid' || isGenerating}
                                className="inline-flex items-center px-2 py-1 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Download"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </button>
                            )}

                            {/* 3-dot Menu */}
                            <div className="relative inline-block text-left">
                              <button
                                onClick={(e) => toggleMenu(policy.id, e)}
                                className="inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                              >
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                </svg>
                              </button>

                              {openMenuId === policy.id && (
                                <>
                                  {/* Backdrop */}
                                  <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setOpenMenuId(null)}
                                  />
                                  
                                  {/* Menu - opens upward to prevent cutoff at bottom of table */}
                                  <div className="absolute right-0 bottom-full mb-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                                    <div className="py-1" role="menu">
                                      <button
                                        onClick={() => handleView(policy)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                      >
                                        <svg className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        View Details
                                      </button>
                                      
                                      <button
                                        onClick={() => handleEdit(policy)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                      >
                                        <svg className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Edit (New Policy)
                                      </button>

                                      <button
                                        onClick={() => handleChangeStatus(policy)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                      >
                                        <svg className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Change Status
                                      </button>

                                      {policy.paymentStatus === 'unpaid' && (
                                        <button
                                          onClick={() => handleMarkAsPaid(policy)}
                                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                        >
                                          <svg className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          Mark as Paid
                                        </button>
                                      )}

                                      {user?.role === 'admin' && (
                                        <button
                                          onClick={() => handleDelete(policy)}
                                          className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center"
                                        >
                                          <svg className="h-4 w-4 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                          Delete
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('policies.totalPolicies')}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {filteredPolicies.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('policies.activePolicies')}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {filteredPolicies.filter(p => p.status === 'active').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('policies.draftPolicies')}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {filteredPolicies.filter(p => p.status === 'draft').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('policies.unpaidPolicies')}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {filteredPolicies.filter(p => p.paymentStatus === 'unpaid').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      {isPreviewOpen && selectedPolicy && (
        <DocumentViewerModal
          isOpen={isPreviewOpen}
          onClose={() => {
            setIsPreviewOpen(false);
            setSelectedPolicy(null);
          }}
          policy={selectedPolicy}
          format="a4"
        />
      )}

      {/* Format Selection Modal */}
      {isFormatModalOpen && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsFormatModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Select Document Format
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Choose the paper size for your document
                    </p>
                    
                    <div className="space-y-3">
                      <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                        style={{ borderColor: selectedFormat === 'a4' ? '#3B82F6' : '#E5E7EB' }}>
                        <input
                          type="radio"
                          name="format"
                          value="a4"
                          checked={selectedFormat === 'a4'}
                          onChange={(e) => setSelectedFormat(e.target.value)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">A4 (210mm × 297mm)</div>
                          <div className="text-xs text-gray-500">Standard size - Most common</div>
                        </div>
                      </label>
                      
                      <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                        style={{ borderColor: selectedFormat === 'a5' ? '#3B82F6' : '#E5E7EB' }}>
                        <input
                          type="radio"
                          name="format"
                          value="a5"
                          checked={selectedFormat === 'a5'}
                          onChange={(e) => setSelectedFormat(e.target.value)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">A5 (148mm × 210mm)</div>
                          <div className="text-xs text-gray-500">Half of A4 - Compact size</div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => handleGenerate(selectedFormat)}
                  disabled={isGenerating}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? 'Generating...' : 'Generate Document'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsFormatModalOpen(false)}
                  disabled={isGenerating}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Modal */}
      {isPaymentModalOpen && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsPaymentModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Mark Policy as Paid
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">
                      Policy: <strong>{selectedPolicyForPayment?.policyNumber}</strong>
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      Select payment method:
                    </p>
                    
                    <div className="space-y-3">
                      <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                        style={{ borderColor: selectedPaymentMethod === 'cash' ? '#10B981' : '#E5E7EB' }}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="cash"
                          checked={selectedPaymentMethod === 'cash'}
                          onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">💵 Cash</div>
                          <div className="text-xs text-gray-500">Payment received in cash</div>
                        </div>
                      </label>
                      
                      <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                        style={{ borderColor: selectedPaymentMethod === 'cheque' ? '#10B981' : '#E5E7EB' }}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="cheque"
                          checked={selectedPaymentMethod === 'cheque'}
                          onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">📝 Cheque</div>
                          <div className="text-xs text-gray-500">Payment by cheque</div>
                        </div>
                      </label>
                      
                      <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                        style={{ borderColor: selectedPaymentMethod === 'on account' ? '#10B981' : '#E5E7EB' }}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="on account"
                          checked={selectedPaymentMethod === 'on account'}
                          onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">🏦 On Account</div>
                          <div className="text-xs text-gray-500">Billed to account</div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleConfirmPayment}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Confirm Payment
                </button>
                <button
                  type="button"
                  onClick={() => setIsPaymentModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {isViewModalOpen && selectedPolicy && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsViewModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Policy Details - {selectedPolicy.policyNumber}
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {/* Client Information */}
                      <div className="col-span-2 border-b pb-2 mb-2">
                        <h4 className="font-semibold text-gray-700">Client Information</h4>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Full Name</label>
                        <p className="text-sm text-gray-900">{`${selectedPolicy.firstName || ''} ${selectedPolicy.fatherName || ''} ${selectedPolicy.grandfatherName || ''} ${selectedPolicy.lastName || ''}`.trim()}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                        <p className="text-sm text-gray-900">{selectedPolicy.dateOfBirth ? moment(selectedPolicy.dateOfBirth).format('DD/MM/YYYY') : 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">National ID</label>
                        <p className="text-sm text-gray-900">{selectedPolicy.nationalId || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Mobile</label>
                        <p className="text-sm text-gray-900">{selectedPolicy.mobile || 'N/A'}</p>
                      </div>
                      <div className="col-span-2">
                        <label className="text-sm font-medium text-gray-500">Address</label>
                        <p className="text-sm text-gray-900">{selectedPolicy.address || 'N/A'}</p>
                      </div>

                      {/* Policy Information */}
                      <div className="col-span-2 border-b pb-2 mb-2 mt-4">
                        <h4 className="font-semibold text-gray-700">Policy Information</h4>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Policy Type</label>
                        <p className="text-sm text-gray-900">{selectedPolicy.policyType || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Status</label>
                        <p className="text-sm">{getStatusBadge(selectedPolicy.status)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Start Date</label>
                        <p className="text-sm text-gray-900">{selectedPolicy.startDate ? moment(selectedPolicy.startDate).format('DD/MM/YYYY HH:mm') : 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">End Date</label>
                        <p className="text-sm text-gray-900">{selectedPolicy.endDate ? moment(selectedPolicy.endDate).format('DD/MM/YYYY HH:mm') : 'N/A'}</p>
                      </div>

                      {/* Vehicle Information (if applicable) */}
                      {selectedPolicy.vehiclePlate && (
                        <>
                          <div className="col-span-2 border-b pb-2 mb-2 mt-4">
                            <h4 className="font-semibold text-gray-700">Vehicle Information</h4>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Vehicle Plate</label>
                            <p className="text-sm text-gray-900">{selectedPolicy.vehiclePlate || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Make & Model</label>
                            <p className="text-sm text-gray-900">{`${selectedPolicy.carMake || ''} ${selectedPolicy.carModel || ''}`.trim() || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Year</label>
                            <p className="text-sm text-gray-900">{selectedPolicy.vehicleYear || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Chassis Number</label>
                            <p className="text-sm text-gray-900">{selectedPolicy.chassisNumber || 'N/A'}</p>
                          </div>
                        </>
                      )}

                      {/* Financial Information */}
                      <div className="col-span-2 border-b pb-2 mb-2 mt-4">
                        <h4 className="font-semibold text-gray-700">Financial Information</h4>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Base Premium</label>
                        <p className="text-sm text-gray-900">{formatCurrency(selectedPolicy.basePremium)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Tax</label>
                        <p className="text-sm text-gray-900">{formatCurrency(selectedPolicy.tax)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Stamp Duty</label>
                        <p className="text-sm text-gray-900">{formatCurrency(selectedPolicy.stampDuty)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Total Premium</label>
                        <p className="text-sm font-semibold text-gray-900">{formatCurrency(selectedPolicy.totalPremium)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Payment Status</label>
                        <p className="text-sm">{getPaymentStatusBadge(selectedPolicy.paymentStatus)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setIsViewModalOpen(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {isStatusModalOpen && selectedPolicyForStatus && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsStatusModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Change Policy Status
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">
                      Policy: <strong>{selectedPolicyForStatus?.policyNumber}</strong>
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      Current Status: <strong>{t(`policies.status.${selectedPolicyForStatus?.status}`)}</strong>
                    </p>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        New Status
                      </label>
                      <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="draft">{t('policies.status.draft')}</option>
                        <option value="yet_to_start">{t('policies.status.yet_to_start')}</option>
                        <option value="active">{t('policies.status.active')}</option>
                        <option value="expired">{t('policies.status.expired')}</option>
                        <option value="void">{t('policies.status.void')}</option>
                        <option value="cancelled">{t('policies.status.cancelled')}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleConfirmStatusChange}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Update Status
                </button>
                <button
                  type="button"
                  onClick={() => setIsStatusModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedPolicyForDelete && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsDeleteModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Delete Policy
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">
                      Are you sure you want to delete policy <strong>{selectedPolicyForDelete?.policyNumber}</strong>?
                    </p>
                    <p className="text-sm text-red-600 font-medium">
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onSuccess={() => {
          setIsBulkUploadModalOpen(false);
          fetchPolicies(); // Refresh the policies list
        }}
      />
    </div>
  );
}


